<script setup>
import { PlusIcon } from "@heroicons/vue/24/outline";

const router = useRouter();
const cartStore = useCartStore();
const contactInfoStore = useContactInfoStore();

const cartItems = computed(() => cartStore.cart);

// filled options with count computed

const filledOptionsWithCount = (participants) => {
  return participants
    .map((option) => {
      if (option.count > 0) {
        return `${option.title} (Per Person) x ${option.count}`;
      }
    })
    .filter((option) => option)
    .join(", ");
};

// back navigation
const handleNavigation = () => {
  if (window.history.length > 1) {
    // User has history
    router.go(-1);
  } else {
    // User does not have history go to home page
    router.push("/");
  }
};

// dialog input form
const isDialogOpen = ref(false);

const openDialog = () => {
  isDialogOpen.value = true;
};

const closeDialog = () => {
  isDialogOpen.value = false;
};

// booking confirmation popup
const isBookingPopupOpen = ref(false);
const isBookingLoading = ref(false);

const openBookingPopup = () => {
  isBookingPopupOpen.value = true;
};

const closeBookingPopup = () => {
  isBookingPopupOpen.value = false;
  isBookingLoading.value = false;
};

// Computed property to check if contactInfo contains text
const isContactInfoFilled = computed(() => {
  return Object.values(contactInfoStore.contactInfo).some(
    (value) => typeof value === "string" && value.trim() !== "",
  );
});

const { api } = useAxios();

// handle verification cart go to payment
const checkAllFields = ref(false);
const showAlert = ref(false);
const isValid = computed(() => {
  const info = contactInfoStore.contactInfo;
  const cart = Array.isArray(cartStore.cart) ? cartStore.cart : [];

  const ishotelNameValid = cart.every(
    (item) => item.participant_details?.hotel_name,
  );
  const isAddressValid = cart.every(
    (item) => item.participant_details?.address,
  );
  return (
    info.first_name &&
    info.last_name &&
    info.phone &&
    info.email &&
    ishotelNameValid &&
    isAddressValid
  );
});
const handleVerification = () => {
  checkAllFields.value = true;
  showAlert.value = !isValid.value;
  if (showAlert.value) {
    setTimeout(() => {
      showAlert.value = false;
    }, 3000);
  }
  return isValid.value;
};

watch(
  [
    contactInfoStore.contactInfo.first_name,
    contactInfoStore.contactInfo.last_name,
    contactInfoStore.contactInfo.phone,
    contactInfoStore.contactInfo.email,
    contactInfoStore.contactInfo.register_to_website,
  ],
  ([first_name, last_name, phone, email]) => {
    if (first_name && last_name && phone && email) {
      checkAllFields.value = false;
    }
  },
);

const handleBookNow = async () => {
  if (!handleVerification()) return;

  // Show booking confirmation popup
  openBookingPopup();
  isBookingLoading.value = true;

  contactInfoStore.contactInfo.payment_method = "pay_as_you_go";
  const dataToSend = {
    cart: cartStore.cart,
    contact_info: contactInfoStore.contactInfo,
  };

  try {
    const response = await api.post("bookings", dataToSend);
    console.log("response", response);

    let booking_trx_id = response.data.data.booking_trx_id;

    // Close popup before navigation
    closeBookingPopup();

    router.push({
      path: `/booking/received/${booking_trx_id}`,
    });
  } catch (error) {
    console.error("Error creating booking:", error);
    // Close popup on error
    closeBookingPopup();
  }
};

const goToPayment = () => {
  if (!handleVerification()) return;
  const dataToSend = {
    cart: cartStore.cart,
    contact_info: contactInfoStore.contactInfo,
  };
  router.push({
    path: "/cart/payment",
    query: { data: JSON.stringify(dataToSend) },
  });
};
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col overflow-y-scroll py-6">
    <div class="relative">
      <div
        v-if="showAlert && !isValid"
        class="animate-fade-in fixed top-4 z-50 mx-2 flex items-center gap-3 rounded-xl border border-orange-300 bg-orange-100 px-6 py-4 text-red-800 shadow-lg md:mx-4"
      >
        <svg
          class="h-6 w-6 text-red-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M18.364 5.636l-12.728 12.728M5.636 5.636l12.728 12.728"
          ></path>
        </svg>
        <span class="font-medium"
          >Please fill in all fields before making payment</span
        >
      </div>
    </div>
    <div class="px-4 sm:px-6">
      <div class="flex items-start justify-between">
        <h2 class="text-base font-semibold leading-6 text-gray-900">Cart</h2>
        <div class="ml-3 flex h-7 items-center">
          <button
            type="button"
            class="relative rounded-md bg-white text-gray-400 hover:text-gray-500"
            @click="handleNavigation"
          >
            <span class="absolute -inset-2.5"></span
            ><span class="sr-only">Close panel</span
            ><svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              aria-hidden="true"
              data-slot="icon"
              class="h-6 w-6"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 18 18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>
      </div>
    </div>
    <div class="relative mt-6 flex-1 flex-col space-y-4 px-4 sm:px-6">
      <div v-for="item in cartItems" class="border-b">
        <h2 class="text-base font-semibold">{{ item.title }}</h2>
        <p>
          {{
            [
              item.additional_tour?.name,
              item.guide_language?.name,
              item.pickup_type?.name,
              item.add_ons?.name,
            ]
              .filter(Boolean)
              .join(", ")
          }}
        </p>

        <p>{{ item.selectedDate }}</p>
        <p>{{ filledOptionsWithCount(item.participants) }}</p>
        <p class="text-base font-medium">
          US$ {{ cartStore.getSubTotalById(item.product_id) }}
        </p>
        <div class="mt-4">
          <div class="mb-2">
            <span
              class="text-sm text-red-800"
              v-if="
                checkAllFields &&
                !item.participant_details.hotel_name &&
                !item.participant_details.address
              "
            >
              Participant Detail cannot be empty!
            </span>
          </div>
          <EnterParticipantDetail v-model="item.participant_details" />
        </div>
      </div>

      <h2 class="w-full pt-3 text-base font-semibold text-gray-800">
        Contact Info
      </h2>
      <div>
        <button
          @click="openDialog"
          class="mb-4 mr-3 cursor-pointer rounded-md bg-white px-3 py-2 text-sm text-primary ring-1 ring-inset ring-primary hover:bg-gray-50"
        >
          <div
            v-if="!isContactInfoFilled"
            class="flex items-center justify-between space-x-1"
          >
            <PlusIcon class="h-5 w-5" aria-hidden="true" />
            <span> Add </span>
          </div>
          <div v-else>
            {{ contactInfoStore.contactInfo.first_name }}
          </div>
        </button>
      </div>

      <div class="rounded-md border-[0.5px] border-[#DDD] px-4 py-6">
        <span
          class="text-sm text-red-800"
          v-if="checkAllFields && !contactInfoStore.contactInfo.first_name"
        >
          First name cannot be empty!
        </span>
        <EnterInfoFormInput
          label="First name"
          v-model="contactInfoStore.contactInfo.first_name"
        />
        <span
          class="text-sm text-red-800"
          v-if="checkAllFields && !contactInfoStore.contactInfo.last_name"
        >
          Last name cannot be empty!
        </span>
        <EnterInfoFormInput
          label="Last name"
          v-model="contactInfoStore.contactInfo.last_name"
        />
        <span
          class="text-sm text-red-800"
          v-if="checkAllFields && !contactInfoStore.contactInfo.phone"
        >
          Phone cannot be empty!
        </span>
        <EnterInfoFormInput
          label="Phone number"
          v-model="contactInfoStore.contactInfo.phone"
        />
        <span
          class="text-sm text-red-800"
          v-if="checkAllFields && !contactInfoStore.contactInfo.email"
        >
          Email cannot be empty!
        </span>
        <EnterInfoFormInput
          label="Email"
          v-model="contactInfoStore.contactInfo.email"
        />
        <div class="flex w-full justify-between">
          <EnterInfoFormInput
            label="Register to website"
            v-model="contactInfoStore.contactInfo.register_to_website"
            type="checkbox"
            readonly
          />
          <div>
            <span
              class="w-36 cursor-pointer text-sm font-medium text-gray-500 underline"
              @click="openDialog"
              >Edit</span
            >
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    class="sticky bottom-0 flex w-full items-center space-x-6 bg-gray-800 px-4 py-3"
  >
    <div class="w-full text-white">
      <div class="mb-2 text-base font-semibold">
        US$
        <span v-if="!cartStore.getTotalCart">-</span>
        <span v-else>{{ cartStore.getTotalCart }}</span>
      </div>

      <div class="flex space-x-4">
        <button
          @click="handleBookNow"
          class="w-full rounded-lg bg-secondary py-2 font-semibold text-white"
        >
          Book Now (pay as you go)
        </button>

        <button
          @click="goToPayment"
          class="w-full rounded-lg bg-secondary-pressed py-2 font-semibold text-white"
        >
          Go to Payment (5% OFF)
        </button>
      </div>
    </div>
  </div>

  <HeadlessTransitionRoot as="template" :show="isDialogOpen">
    <HeadlessDialog class="relative z-10" @close="closeDialog">
      <HeadlessTransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        />
      </HeadlessTransitionChild>

      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div
          class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0"
        >
          <HeadlessTransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <HeadlessDialogPanel
              class="relative min-w-[20rem] transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg"
            >
              <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div class="sm:flex sm:items-start">
                  <div class="mt-3 sm:ml-4 sm:mt-0 sm:text-left">
                    <HeadlessDialogTitle
                      as="h3"
                      class="text-base font-semibold leading-6 text-gray-900"
                    >
                      Add Contact Info
                    </HeadlessDialogTitle>
                    <EnterInfoFormInput
                      label="First Name"
                      v-model="contactInfoStore.contactInfo.first_name"
                      edit
                    />
                    <EnterInfoFormInput
                      label="Last name"
                      v-model="contactInfoStore.contactInfo.last_name"
                      edit
                    />

                    <EnterInfoFormInput
                      label="Phone number"
                      v-model="contactInfoStore.contactInfo.phone"
                      edit
                    />

                    <EnterInfoFormInput
                      label="Email"
                      v-model="contactInfoStore.contactInfo.email"
                      edit
                    />

                    <EnterInfoFormInput
                      label="Register to website"
                      v-model="contactInfoStore.contactInfo.register_to_website"
                      type="checkbox"
                    />
                  </div>
                </div>
              </div>
              <div
                class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6"
              >
                <button
                  type="button"
                  class="hover:primary-hover inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto"
                  @click="closeDialog"
                >
                  Save
                </button>
                <button
                  type="button"
                  class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  ref="cancelButtonRef"
                  @click="closeDialog"
                >
                  Cancel
                </button>
              </div>
            </HeadlessDialogPanel>
          </HeadlessTransitionChild>
        </div>
      </div>
    </HeadlessDialog>
  </HeadlessTransitionRoot>

  <!-- Booking Confirmation Popup -->
  <HeadlessTransitionRoot as="template" :show="isBookingPopupOpen">
    <HeadlessDialog class="relative z-10">
      <HeadlessTransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        />
      </HeadlessTransitionChild>

      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div
          class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0"
        >
          <HeadlessTransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <HeadlessDialogPanel
              class="relative min-w-[20rem] transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg"
            >
              <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div class="sm:flex sm:items-start">
                  <div
                    class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10"
                    v-if="!isBookingLoading"
                  >
                    <svg
                      class="h-6 w-6 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  </div>
                  <div
                    class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10"
                    v-else
                  >
                    <svg
                      class="h-6 w-6 animate-spin text-blue-600"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      ></circle>
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  </div>
                  <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <HeadlessDialogTitle
                      as="h3"
                      class="text-base font-semibold leading-6 text-gray-900"
                    >
                      <span v-if="isBookingLoading"
                        >Processing Your Booking</span
                      >
                      <span v-else>Booking Confirmed!</span>
                    </HeadlessDialogTitle>
                    <div class="mt-2">
                      <p class="text-sm text-gray-500" v-if="isBookingLoading">
                        Please wait while we process your booking request. This
                        may take a few moments...
                      </p>
                      <p class="text-sm text-gray-500" v-else>
                        Your booking has been successfully processed. You will
                        be redirected to the confirmation page shortly.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </HeadlessDialogPanel>
          </HeadlessTransitionChild>
        </div>
      </div>
    </HeadlessDialog>
  </HeadlessTransitionRoot>
</template>
